import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, Megaphone, Trash2, X, AlertTriangle, Mail, MailCheck, CheckCircle2, RefreshCw } from "lucide-react";
import { PosterHealth } from "./PosterHealth";
import { QueueNav } from "./QueueNav";
import ProfilePanel from "./ProfilePanel";
import AnnouncementTemplate from "./AnnouncementTemplate";
import { fmtDate, Thumb } from "./AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminArtistView({ recordId, token, onBack, onChanged, onLogout, queue = [], queueLabel = "", onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingAnnounced, setTogglingAnnounced] = useState(false);
  const [welcomeState, setWelcomeState] = useState("idle");

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/admin/artist/${recordId}`, { headers: authHeader })
      .then((res) => { if (active) setData(res.data); })
      .catch((err) => {
        if (err.response?.status === 401) onLogout();
        else if (active) setError(err.response?.status === 404 ? "Artist not found." : "Failed to load artist.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [recordId]);

  const qIndex = queue.indexOf(recordId);
  const queueNav = queue.length > 1 && qIndex >= 0 ? {
    index: qIndex,
    total: queue.length,
    label: queueLabel,
    onPrev: qIndex > 0 ? () => onNavigate(queue[qIndex - 1]) : null,
    onNext: qIndex < queue.length - 1 ? () => onNavigate(queue[qIndex + 1]) : null,
  } : null;

  const handleProfileUpdate = (_idx, updated) => {
    setData((d) => ({ ...d, profile: { ...d.profile, ...updated } }));
    onChanged();
  };

  const reload = () =>
    axios.get(`${API}/admin/artist/${recordId}`, { headers: authHeader }).then((res) => setData(res.data)).catch(() => {});

  const resendWelcome = async () => {
    setWelcomeState("sending");
    try {
      await axios.post(`${API}/admin/artist/${recordId}/welcome`, {}, { headers: authHeader });
      setWelcomeState("sent");
      setTimeout(() => setWelcomeState("idle"), 5000);
    } catch (err) {
      alert(err.response?.data?.detail || "Could not send the welcome email.");
      setWelcomeState("idle");
    }
  };

  const toggleAnnounced = async () => {
    const next = !data.profile.announced;
    setTogglingAnnounced(true);
    try {
      const res = await axios.patch(`${API}/admin/artist/${recordId}/announced`, { announced: next }, { headers: authHeader });
      setData((d) => ({ ...d, profile: { ...d.profile, announced: res.data.announced } }));
      onChanged();
    } catch {
      alert("Could not update ANNOUNCED? in Airtable. Please try again.");
    } finally {
      setTogglingAnnounced(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/artist/${recordId}`, { headers: authHeader });
      onChanged();
      onBack();
    } catch (err) {
      alert(err.response?.data?.detail || "Delete failed. Please try again.");
      setDeleting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="loading-screen" data-testid="admin-artist-loading">
        <Loader2 size={40} className="spin gold" />
        <p>LOADING ARTIST...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="error-screen" data-testid="admin-artist-error">
        <p>{error}</p>
        <button className="btn-primary" onClick={onBack}>BACK TO ROSTER</button>
      </div>
    );
  }

  const { profile, account, created_time, options, poster_health = [] } = data;

  if (showAnnounce) {
    return (
      <AnnouncementTemplate
        onBack={() => setShowAnnounce(false)}
        profiles={[profile]}
        servicesOptions={options.session_services_offered}
        token={token}
        adminTools={{
          announced: profile.announced,
          onToggle: toggleAnnounced,
          busy: togglingAnnounced,
          queue: queueNav,
          onPushed: () => { reload(); onChanged(); },
        }}
      />
    );
  }

  return (
    <div className="dashboard admin-dash" data-testid="admin-artist-view">
      <header className="dash-header">
        <div className="admin-header-left">
          <button className="btn-back" onClick={onBack} data-testid="admin-back-btn">
            <ArrowLeft size={16} /> ROSTER
          </button>
          {queueNav && <QueueNav nav={queueNav} />}
        </div>
        <div className="dash-header-right">
          <span className="admin-badge">ADMIN</span>
          <button className="btn-announce" onClick={() => setShowAnnounce(true)} data-testid="admin-announce-btn">
            <Megaphone size={15} /> ANNOUNCEMENT POSTER
          </button>
          {!account.has_account && (
            <button className="btn-announce btn-admin-link" onClick={resendWelcome} disabled={welcomeState === "sending"} data-testid="resend-welcome-btn">
              {welcomeState === "sending" ? <RefreshCw size={15} className="spin" /> : welcomeState === "sent" ? <MailCheck size={15} /> : <Mail size={15} />}
              {welcomeState === "sending" ? "SENDING..." : welcomeState === "sent" ? "WELCOME EMAIL SENT" : "RESEND WELCOME EMAIL"}
            </button>
          )}
          <button className="btn-danger" onClick={() => setConfirmDelete(true)} data-testid="admin-delete-btn">
            <Trash2 size={15} /> DELETE ARTIST
          </button>
        </div>
      </header>

      <section className="admin-artist-hero" data-testid="admin-artist-hero">
        <Thumb src={profile.photos?.[0]?.thumbnails?.large?.url || profile.photos?.[0]?.url} name={profile.full_name} size={120} />
        <div className="admin-artist-meta">
          <h1 className="admin-artist-name" data-testid="admin-artist-name">{profile.full_name || "Unnamed"}</h1>
          <p className="admin-artist-band">{profile.band_name || "—"}</p>
          <div className="admin-meta-row">
            <span className="admin-meta"><b>EMAIL</b> {profile.email_address || "—"}</span>
            <span className="admin-meta"><b>JOINED ROSTER</b> {fmtDate(created_time)}</span>
            <span className="admin-meta"><b>PORTAL</b> {account.has_account ? `Active since ${fmtDate(account.created_at)}` : "No account yet"}</span>
            <span className="admin-meta"><b>HIREABLE</b> {profile.hireable_for_sessions ? "Yes" : "No"}</span>
            <span className="admin-meta" data-testid="admin-announced-meta">
              <b>ANNOUNCED</b>
              <button className={`pill pill-btn ${profile.announced ? "pill-green" : "pill-muted"}`} onClick={toggleAnnounced} disabled={togglingAnnounced} data-testid="hero-announced-toggle">
                {profile.announced ? "YES" : "NO"}
              </button>
            </span>
            <span className="admin-meta" data-testid="admin-poster-meta"><b>POSTERS</b> {profile.announcement_poster?.length ? `${profile.announcement_poster.length} in Airtable` : "None yet"}</span>
          </div>
        </div>
      </section>

      <main className="dash-content">
        <PosterHealth
          items={poster_health}
          recordId={recordId}
          token={token}
          onChanged={() => { reload(); onChanged(); }}
          onCreatePoster={() => setShowAnnounce(true)}
        />
        <ProfilePanel
          key={profile.record_id}
          profile={profile}
          token={token}
          panelIndex={0}
          onUpdate={handleProfileUpdate}
          servicesOptions={options.session_services_offered}
        />
      </main>

      {confirmDelete && (
        <div className="feed-modal-backdrop" onClick={() => !deleting && setConfirmDelete(false)} data-testid="delete-confirm-modal">
          <div className="share-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="feed-modal-head">
              <div>
                <p className="feed-modal-kicker danger-text">DELETE ARTIST</p>
                <h3 className="feed-modal-title">Remove {profile.full_name}?</h3>
              </div>
              <button className="feed-modal-close" onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid="delete-cancel-x">
                <X size={18} />
              </button>
            </div>
            <div className="danger-box">
              <AlertTriangle size={18} />
              <p>This permanently deletes the Airtable record (photos, posters, all details) and removes their portal login. This cannot be undone.</p>
            </div>
            <div className="share-actions">
              <button className="btn-danger btn-danger-solid" onClick={handleDelete} disabled={deleting} data-testid="delete-confirm-btn">
                {deleting ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />} {deleting ? "DELETING..." : "YES, DELETE PERMANENTLY"}
              </button>
              <button className="share-btn share-ghost" onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid="delete-cancel-btn">
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
